import {
  Box,
  Grid2,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { isEmpty } from 'lodash';

import { CompoundHeader } from '@/components/display/CompoundHeader/CompoundHeader';
import { Medallion } from '@/components/display/Medallion/Medallion';
import { Card } from '@/components/layout/Card/Card';
import { COLORS } from '@/styles/tokens/colors';

import {
  EntityCoverSlideDataPoint,
  EntityCoverSlideDetailsList,
  EntityCoverSlideDetailsListVariant,
} from './EntityCoverSlide.types';

export interface EntityHeaderProps {
  title: string;
  subheading: string;
}
export function EntityHeader({ title, subheading }: EntityHeaderProps) {
  const theme = useTheme();

  return (
    <CompoundHeader
      heading={title}
      subheading={subheading}
      headingProps={{
        variant: 'h1_display',
        color: theme.palette.primary.main,
      }}
      subheadingProps={{
        sx: { fontSize: '1rem', lineHeight: '2rem', fontWeight: 500 },
      }}
      invertLabel
    />
  );
}
export interface EntityDataPointsProps {
  dataPoints: EntityCoverSlideDataPoint[];
}
export function EntityDataPoints({ dataPoints }: EntityDataPointsProps) {
  return (
    <Grid2 container spacing={2} columns={12}>
      {dataPoints.map((dataPoint, idx) => (
        <Grid2 key={idx} size={4}>
          <EntityDataPoint {...dataPoint} />
        </Grid2>
      ))}
    </Grid2>
  );
}

export function EntityDataPoint({
  label,
  value,
  subText,
  Icon,
}: EntityCoverSlideDataPoint) {
  const theme = useTheme();
  return (
    <Stack direction="row" spacing={1}>
      <Box sx={{ p: 0.5 }}>
        <Medallion
          icon={<Icon />}
          size={40}
          colors={{
            background: COLORS.GRAY[100],
            iconColor: COLORS.TEXT.SECONDARY,
          }}
        />
      </Box>
      <Stack spacing={0.5}>
        <Stack>
          <Typography
            variant="title-xs"
            component="span"
            color={COLORS.TEXT.SECONDARY}
            sx={{ fontSize: '1rem', lineHeight: '2rem', fontWeight: 500 }}
          >
            {label}
          </Typography>
          <Typography
            variant="title-xl"
            component="span"
            color={theme.palette.primary.main}
          >
            {value}
          </Typography>
        </Stack>
        {subText && (
          <Typography
            variant="body-xs"
            sx={{ fontSize: '1rem', lineHeight: '2rem', fontWeight: 500 }}
          >
            {subText}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

export interface EntityDetailsListsProps {
  lists: EntityCoverSlideDetailsList[];
  variant?: EntityCoverSlideDetailsListVariant;
}

export function EntityDetailsLists({
  lists,
  variant = EntityCoverSlideDetailsListVariant.TwoColumn,
}: EntityDetailsListsProps) {
  if (isEmpty(lists)) return null;

  return (
    <Card variant="filled" sx={{ p: 2 }}>
      <Grid2 container spacing={6} columns={variant}>
        {lists.map((list, idx) => (
          <EntityDetailsList key={idx} list={list} variant={variant} />
        ))}
      </Grid2>
    </Card>
  );
}
interface EntityDetailsListProps {
  list: EntityCoverSlideDetailsList;
  variant: EntityCoverSlideDetailsListVariant;
}

const TABLE_CELL_WIDTH: Record<EntityCoverSlideDetailsListVariant, string> = {
  [EntityCoverSlideDetailsListVariant.OneColumn]: '15%',
  [EntityCoverSlideDetailsListVariant.TwoColumn]: '25%',
  [EntityCoverSlideDetailsListVariant.ThreeColumn]: '40%',
};

function EntityDetailsList({ list, variant }: EntityDetailsListProps) {
  return (
    <Grid2 size={1}>
      <Stack spacing={1}>
        <Typography
          variant="title-xs"
          sx={{ fontSize: '1rem', lineHeight: '2rem', fontWeight: 500 }}
        >
          {list.title}
        </Typography>
        <Table>
          <TableBody>
            {list.entries.map(({ label, value }, idx) => (
              <TableRow key={`label-${idx}`}>
                <TableCell
                  width={TABLE_CELL_WIDTH[variant]}
                  padding="none"
                  sx={{
                    borderBottom: 'none',
                    textWrap: 'nowrap',
                    py: 0.5,
                    pr: 2,
                  }}
                  colSpan={value ? 1 : 2}
                >
                  <Typography
                    variant="body-xs"
                    sx={{
                      fontSize: '1rem',
                      lineHeight: '2rem',
                      fontWeight: 500,
                    }}
                  >
                    {label}
                  </Typography>
                </TableCell>
                <TableCell
                  padding="none"
                  align="left"
                  sx={{ borderBottom: 'none', py: 0.5 }}
                >
                  <Typography
                    variant="title-xs"
                    component="p"
                    sx={{
                      fontSize: '1rem',
                      lineHeight: '2rem',
                      fontWeight: 500,
                    }}
                  >
                    {value}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Stack>
    </Grid2>
  );
}
